import { registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localeKo from '@angular/common/locales/ko';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
} from '@angular/core';
import { COMPOSITION_BUFFER_MODE } from '@angular/forms';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  MAT_TIME_LOCALE,
  MatNativeDateTimeModule,
  MatTimepickerIntl,
  MatTimepickerModule,
} from '@dhutaryan/ngx-mat-timepicker';
import { FullCalendarModule } from '@fullcalendar/angular';
import { provideFuse } from '@fuse/fuse.provider';
import { DialogModule } from '@libs/shared/ui/dialogs/dialog.module';
import { EnterNewlineTextareaDirective } from '@libs/shared/utils/directives/enter-newline-textarea.directive';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { IMAGE_RESIZE_URL } from '@libs/shared/tokens/image-resize-url.token';
import { appRoutes } from '@sprint-expert/app/app.routes';
import { provideApi } from '@sprint-expert/app/core/api/api.provider';
import { provideAuth } from '@sprint-expert/app/core/auth/auth.provider';
import { provideIcons } from '@sprint-expert/app/core/icons/icons.provider';
import { TranslocoHttpLoader } from '@sprint-expert/app/core/transloco/transloco.http-loader';
import { HammerConfig } from '@sprint-expert/app/hammer-config.service';
import { mockApiServices } from '@sprint-expert/app/mock-api';
import { provideNgrxStore } from '@sprint-expert/app/state/state.provider';
import { environment } from '@sprint-expert/environments/environment';
import { provideEcharts } from 'ngx-echarts';
import { provideScrollbarOptions } from 'ngx-scrollbar';
import { firstValueFrom } from 'rxjs';

registerLocaleData(localeKo);

export class MatTimepickerIntlKo {
  inputsTitle = '시간 입력';
  dialsTitle = '시간 선택';
  hourInputHint = '시간 입력';
  minuteInputHint = '분 입력';
  openTimepickerLabel = '시간 선택';
  closeTimepickerLabel = '시간 선택 닫기';
  okButton = '확인';
  cancelButton = '취소';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideEffects(),
    provideStore({ router: routerReducer }),
    provideStoreDevtools(),
    provideEcharts(),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      HammerModule,
      MatSnackBarModule,
      DialogModule,
      FullCalendarModule,
      MatTimepickerModule,
      MatNativeDateTimeModule,
      TranslateModule.forRoot(),
      EnterNewlineTextareaDirective
    ),
    provideHttpClient(),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideRouterStore(),
    provideApi(),
    provideAuth(),
    provideNgrxStore(),
    provideScrollbarOptions({
      visibility: 'hover',
      appearance: 'compact',
    }),
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'ko',
            label: '한국어',
          },
        ],
        defaultLang: 'ko',
        fallbackLang: 'ko',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(defaultLang);

        return () => firstValueFrom(translocoService.load(defaultLang));
      },
      multi: true,
    },
    provideIcons(),
    {
      provide: COMPOSITION_BUFFER_MODE,
      useValue: false,
    },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    { provide: LOCALE_ID, useValue: 'ko-KR' },
    { provide: MAT_TIME_LOCALE, useValue: 'ko-KR' },

    {
      provide: MatTimepickerIntl,
      useClass: MatTimepickerIntlKo,
    },

    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'yyyy-MM-dd',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'D',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    {
      provide: 'baseS3Path',
      useValue: environment.s3Path,
    },
    {
      provide: IMAGE_RESIZE_URL,
      useValue: environment.imageResizeUrl,
    },
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: 'with-navigation',
        scheme: 'auto',
        screens: {
          sm: '600px',
          md: '768px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-brand',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
